import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    SwipeableDrawer,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Search as SearchIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Highlight as HighlightIcon,
} from '@mui/icons-material';

const RoomCard = ({ room, isSelected, onSelect, onRename, onDelete }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(room.name);

    const handleStartEdit = (e) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditName(room.name);
    };

    const handleSaveEdit = async () => {
        if (editName.trim() && editName !== room.name) {
            try {
                await onRename(room.id, editName.trim());
                setIsEditing(false);
            } catch (error) {
                console.error('Failed to rename room:', error);
            }
        } else {
            setIsEditing(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditName(room.name);
    };

    const handleDelete = async (e) => {
        e.stopPropagation();
        try {
            await onDelete(room.id);
        } catch (error) {
            console.error('Failed to delete room:', error);
        }
    };

    return (
        <Card
            sx={{
                mb: 1.5,
                width: '100%',
                cursor: 'pointer',
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                transition: 'all 0.2s',
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 2,
                },
            }}
            onClick={() => !isEditing && onSelect(room.id)}
        >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isSelected && <HighlightIcon color="primary" fontSize="small" />}

                    {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flex: 1, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSaveEdit();
                                    } else if (e.key === 'Escape') {
                                        handleCancelEdit();
                                    }
                                }}
                                autoFocus
                                fullWidth
                                onClick={(e) => e.stopPropagation()}
                            />
                            <Tooltip title="Save">
                                <IconButton
                                    size="small"
                                    color="success"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveEdit();
                                    }}
                                >
                                    <CheckIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelEdit();
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <>
                            <Typography
                                variant="body1"
                                sx={{
                                    flex: 1,
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isSelected ? 'primary.main' : 'text.primary',
                                }}
                            >
                                {room.name}
                            </Typography>
                            <Tooltip title="Rename">
                                <IconButton
                                    size="small"
                                    onClick={handleStartEdit}
                                    sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={handleDelete}
                                    sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

const RoomListContent = ({
    rooms,
    selectedRoomId,
    searchQuery,
    onSearchChange,
    onSelectRoom,
    onRenameRoom,
    onDeleteRoom,
}) => {
    const filteredRooms = rooms
        .filter((room) => room.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                        Rooms
                    </Typography>
                    <Chip label={rooms.length} color="primary" size="small" />
                </Box>

                {/* Search Bar */}
                <TextField
                    size="small"
                    placeholder="Search rooms..."
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Room List */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {filteredRooms.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: 'text.secondary',
                        }}
                    >
                        <Typography variant="body2">
                            {searchQuery ? 'No rooms found' : 'No rooms yet'}
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0, width: '100%' }}>
                        {filteredRooms.map((room) => (
                            <ListItem key={room.id} sx={{ p: 0, width: '100%', display: 'block' }}>
                                <RoomCard
                                    room={room}
                                    isSelected={selectedRoomId === room.id}
                                    onSelect={onSelectRoom}
                                    onRename={onRenameRoom}
                                    onDelete={onDeleteRoom}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );
};

const RoomListSidebar = ({
    variant = 'fixed', // 'fixed' or 'drawer'
    open = true,
    onToggle,
    rooms = [],
    selectedRoomId,
    searchQuery,
    onSearchChange,
    onSelectRoom,
    onRenameRoom,
    onDeleteRoom,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Force drawer variant on mobile
    const effectiveVariant = isMobile ? 'drawer' : variant;

    if (effectiveVariant === 'drawer') {
        return (
            <SwipeableDrawer
                anchor="bottom"
                open={open}
                onClose={onToggle}
                onOpen={onToggle}
                disableSwipeToOpen
                sx={{
                    '& .MuiDrawer-paper': {
                        height: '60vh',
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                    },
                }}
            >
                <RoomListContent
                    rooms={rooms}
                    selectedRoomId={selectedRoomId}
                    searchQuery={searchQuery}
                    onSearchChange={onSearchChange}
                    onSelectRoom={onSelectRoom}
                    onRenameRoom={onRenameRoom}
                    onDeleteRoom={onDeleteRoom}
                />
            </SwipeableDrawer>
        );
    }

    // Fixed sidebar for desktop/tablet
    return (
        <Box
            sx={{
                height: '100%',
                backgroundColor: 'background.paper',
                borderLeft: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <RoomListContent
                rooms={rooms}
                selectedRoomId={selectedRoomId}
                searchQuery={searchQuery}
                onSearchChange={onSearchChange}
                onSelectRoom={onSelectRoom}
                onRenameRoom={onRenameRoom}
                onDeleteRoom={onDeleteRoom}
            />
        </Box>
    );
};

export default RoomListSidebar;
